import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronDown, Package, Truck, CheckCircle, Clock, XCircle, ShieldAlert, Hash, Check, X, ShoppingCart, Link2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const statusConfig = {
  pending_payment: { label: 'Pending Payment', color: 'bg-[#1E2532] text-[#A1A7B5]', icon: Clock },
  awaiting_payment: { label: 'Unpaid', color: 'bg-[#1E2532] text-[#A1A7B5]', icon: ShoppingCart },
  pending_verification: { label: 'Needs Verification', color: 'bg-orange-500/20 text-orange-400', icon: ShieldAlert },
  processing: { label: 'Processing', color: 'bg-yellow-500/20 text-yellow-400', icon: Package },
  shipped: { label: 'Shipped', color: 'bg-blue-500/20 text-blue-400', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/20 text-red-400', icon: XCircle },
};

const AdminOrdersPage = () => {
  const [tab, setTab] = useState('orders'); // 'orders' | 'abandoned'
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [abandonedCount, setAbandonedCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [actioningId, setActioningId] = useState(null);
  const [payLinkId, setPayLinkId] = useState(null);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      const effectiveStatus = tab === 'abandoned' ? 'awaiting_payment' : filter;
      if (effectiveStatus) params.append('status', effectiveStatus);
      const { data } = await axios.get(`${API}/admin/orders?${params}`, { withCredentials: true });
      const sorted = [...(data.orders || [])].sort((a, b) => {
        const av = a.status === 'pending_verification' ? 0 : 1;
        const bv = b.status === 'pending_verification' ? 0 : 1;
        return av - bv;
      });
      setOrders(sorted);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchAbandonedCount = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/orders?status=awaiting_payment&limit=1`, { withCredentials: true });
      setAbandonedCount(data.total || 0);
    } catch { /* non-critical */ }
  };

  useEffect(() => { fetchOrders(); }, [page, filter, tab]);
  useEffect(() => { fetchAbandonedCount(); }, []);

  const switchTab = (t) => {
    if (t === tab) return;
    setTab(t);
    setFilter('');
    setPage(1);
    setExpandedId(null);
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      const { data } = await axios.put(`${API}/admin/orders/${orderId}/status`, { status: newStatus }, { withCredentials: true });
      let msg = `Order updated to ${newStatus}`;
      if (data.tracking_number) msg += ` - Tracking: ${data.tracking_number}`;
      if (data.shipstation_warning) toast.warning(`Status updated but ShipStation: ${data.shipstation_warning}`);
      else toast.success(msg);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const confirmPayment = async (orderId) => {
    setActioningId(orderId);
    try {
      await axios.post(`${API}/admin/orders/${orderId}/confirm-payment`, {}, { withCredentials: true });
      toast.success('Payment confirmed — order is now processing. Confirmation email, ShipStation & commission triggered.');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm payment');
    } finally {
      setActioningId(null);
    }
  };

  const rejectPayment = async (orderId) => {
    if (!window.confirm('Reject this Zelle payment? The order will be cancelled and stock restored.')) return;
    setActioningId(orderId);
    try {
      await axios.post(`${API}/admin/orders/${orderId}/reject-payment`, {}, { withCredentials: true });
      toast.success('Payment rejected — order cancelled and stock restored.');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject payment');
    } finally {
      setActioningId(null);
    }
  };

  const sendPaymentLink = async (order) => {
    const orderId = order.id;
    if (['processing', 'shipped', 'delivered'].includes(order.status)) {
      if (!window.confirm(`This order is marked "${order.status}". Send a payment link and reopen it for payment? Use this only if the customer hasn't actually paid yet.`)) return;
    }
    setPayLinkId(orderId);
    try {
      const { data } = await axios.post(`${API}/admin/orders/${orderId}/send-payment-link`, {}, { withCredentials: true });
      try { await navigator.clipboard.writeText(data.payment_link); } catch { /* clipboard may be blocked */ }
      toast.success(data.email_sent ? 'Payment link emailed to customer & copied to clipboard' : 'Payment link created & copied to clipboard');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send payment link');
    } finally {
      setPayLinkId(null);
    }
  };

  const filters = [
    { label: 'All Orders', value: '' },
    { label: 'Needs Verification', value: 'pending_verification' },
    { label: 'Processing', value: 'processing' },
    { label: 'Shipped', value: 'shipped' },
    { label: 'Delivered', value: 'delivered' },
    { label: 'Cancelled', value: 'cancelled' },
  ];

  const isAbandoned = tab === 'abandoned';

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="admin-orders-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="flex items-center space-x-2 text-[#A1A7B5] hover:text-white mb-6 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Dashboard</span>
        </Link>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {isAbandoned ? 'Abandoned Carts' : 'Order Management'}
          </h1>
          <span className="text-[#A1A7B5]">{total} {isAbandoned ? 'abandoned' : 'total'}</span>
        </div>

        {/* Top-level tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#1E2532]">
          <button
            onClick={() => switchTab('orders')}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              !isAbandoned ? 'border-[#00F0FF] text-white' : 'border-transparent text-[#A1A7B5] hover:text-white'
            }`}
            data-testid="tab-orders"
          >
            Orders
          </button>
          <button
            onClick={() => switchTab('abandoned')}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-2 ${
              isAbandoned ? 'border-[#00F0FF] text-white' : 'border-transparent text-[#A1A7B5] hover:text-white'
            }`}
            data-testid="tab-abandoned"
          >
            Abandoned Carts
            {abandonedCount > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#1E2532] text-[#A1A7B5]">{abandonedCount}</span>
            )}
          </button>
        </div>

        {isAbandoned && (
          <p className="text-[#A1A7B5] text-sm mb-6" data-testid="abandoned-help">
            These customers reached checkout but never submitted a Zelle payment. They are not real orders — no payment is expected. Stock is still reserved until they pay or the cart is cancelled.
          </p>
        )}

        {/* Status Filters (Orders tab only) */}
        {!isAbandoned && (
          <div className="flex flex-wrap gap-2 mb-6">
            {filters.map((f) => (
              <button
                key={f.value}
                onClick={() => { setFilter(f.value); setPage(1); }}
                className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                  filter === f.value
                    ? 'bg-[#00F0FF] text-black font-medium'
                    : 'bg-[#111621] text-[#A1A7B5] hover:bg-[#1E2532]'
                }`}
                data-testid={`filter-${f.value || 'all'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : orders.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <p className="text-[#A1A7B5]">{isAbandoned ? 'No abandoned carts' : 'No orders found'}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const config = statusConfig[order.status] || statusConfig.awaiting_payment;
              const StatusIcon = config.icon;
              const isExpanded = expandedId === order.id;
              const needsVerification = order.status === 'pending_verification';
              const isActioning = actioningId === order.id;

              return (
                <div
                  key={order.id}
                  className={`glass-card overflow-hidden ${needsVerification ? 'ring-1 ring-orange-500/40' : ''}`}
                  data-testid={`order-row-${order.id}`}
                >
                  {/* Order Header */}
                  <div
                    className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 cursor-pointer hover:bg-[#111621]/50 transition-colors"
                    onClick={() => setExpandedId(isExpanded ? null : order.id)}
                  >
                    <div className="flex items-center space-x-4 min-w-0">
                      <StatusIcon className={`w-5 h-5 flex-shrink-0 ${config.color.split(' ')[1]}`} />
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate">
                          {order.customer_name || 'Guest'}
                          <span className="text-[#A1A7B5] text-sm ml-2">({order.customer_email})</span>
                        </p>
                        <p className="text-[#A1A7B5] text-xs">
                          {order.order_number ? <span className="font-mono text-[#00F0FF]">{order.order_number}</span> : null}
                          {order.order_number ? ' · ' : ''}
                          {new Date(order.created_at).toLocaleString()} - {order.items?.length || 0} item(s)
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 sm:flex-shrink-0 pl-9 sm:pl-0">
                      <span className="text-white font-bold">${order.total?.toFixed(2)}</span>
                      <span className={`text-xs px-3 py-1 rounded-full ${config.color}`}>
                        {config.label}
                      </span>
                      <Button
                        onClick={(e) => { e.stopPropagation(); sendPaymentLink(order); }}
                        disabled={payLinkId === order.id}
                        className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 border border-purple-500/40 text-xs px-3 py-1"
                        data-testid={`send-pay-link-${order.id}`}
                      >
                        <Link2 className="w-3.5 h-3.5 mr-1" /> {payLinkId === order.id ? 'Sending…' : 'Pay Link'}
                      </Button>
                      {!isAbandoned && (needsVerification ? (
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={(e) => { e.stopPropagation(); confirmPayment(order.id); }}
                            disabled={isActioning}
                            className="bg-green-500 hover:bg-green-600 text-black text-xs px-3 py-1 font-semibold"
                            data-testid={`confirm-payment-${order.id}`}
                          >
                            <Check className="w-3.5 h-3.5 mr-1" /> Confirm
                          </Button>
                          <Button
                            onClick={(e) => { e.stopPropagation(); rejectPayment(order.id); }}
                            disabled={isActioning}
                            className="bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs px-3 py-1 border border-red-500/40"
                            data-testid={`reject-payment-${order.id}`}
                          >
                            <X className="w-3.5 h-3.5 mr-1" /> Reject
                          </Button>
                        </div>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              className="bg-[#1E2532] hover:bg-[#2a3040] text-white text-xs px-3 py-1"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`update-status-${order.id}`}
                            >
                              Update <ChevronDown className="w-3 h-3 ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-[#0C0E14] border-[#1E2532]">
                            {['processing', 'shipped', 'delivered', 'cancelled'].map((s) => (
                              <DropdownMenuItem
                                key={s}
                                onClick={(e) => { e.stopPropagation(); updateStatus(order.id, s); }}
                                className="text-white hover:text-[#00F0FF] hover:bg-white/5 capitalize"
                              >
                                {s}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ))}
                    </div>
                  </div>

                  {/* Zelle verification banner */}
                  {needsVerification && !isAbandoned && (
                    <div className="border-t border-orange-500/20 px-4 py-3 bg-orange-500/5 flex flex-col gap-2" data-testid={`zelle-verify-banner-${order.id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
                        <div className="flex items-center gap-2 text-sm">
                          <Hash className="w-4 h-4 text-[#00F0FF]" />
                          <span className="text-[#A1A7B5]">Payment note / memo:</span>
                          <span className="text-white font-mono" data-testid={`payment-note-${order.id}`}>{order.order_number}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Hash className="w-4 h-4 text-orange-400" />
                          <span className="text-[#A1A7B5]">Zelle confirmation #:</span>
                          <span className="text-white font-mono" data-testid={`zelle-confirmation-${order.id}`}>
                            {order.zelle_confirmation_number || 'Not provided'}
                          </span>
                        </div>
                        {order.zelle_submitted_at && (
                          <span className="text-[#6B7280] text-xs">
                            Submitted {new Date(order.zelle_submitted_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                      <span className="text-orange-400 text-xs">
                        Find this payment note in your bank&apos;s Zelle activity, confirm the amount matches, then click Confirm to ship.
                      </span>
                    </div>
                  )}

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-[#1E2532] p-4 bg-[#111621]/30">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Items */}
                        <div>
                          <h4 className="text-[#A1A7B5] text-sm mb-3">{isAbandoned ? 'Cart Items' : 'Order Items'}</h4>
                          <div className="space-y-2">
                            {order.items?.map((item, i) => (
                              <div key={i} className="flex justify-between text-sm">
                                <span className="text-white">{item.name} x{item.quantity}</span>
                                <span className="text-[#A1A7B5]">${item.subtotal?.toFixed(2)}</span>
                              </div>
                            ))}
                            <div className="border-t border-[#1E2532] pt-2 mt-2">
                              {order.subtotal && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-[#A1A7B5]">Subtotal</span>
                                  <span className="text-white">${order.subtotal?.toFixed(2)}</span>
                                </div>
                              )}
                              {order.shipping_cost !== undefined && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-[#A1A7B5]">Shipping ({order.shipping_method})</span>
                                  <span className="text-white">${order.shipping_cost?.toFixed(2)}</span>
                                </div>
                              )}
                              <div className="flex justify-between text-sm font-bold mt-1">
                                <span className="text-white">Total</span>
                                <span className="text-[#00F0FF]">${order.total?.toFixed(2)}</span>
                              </div>
                            </div>
                            {!isAbandoned && (
                              <div className="border-t border-[#1E2532] pt-2 mt-2 space-y-1">
                                <div className="flex justify-between text-xs">
                                  <span className="text-[#A1A7B5]">Payment Method</span>
                                  <span className="text-white capitalize">{order.payment_method || 'zelle'}</span>
                                </div>
                                <div className="flex justify-between text-xs">
                                  <span className="text-[#A1A7B5]">Payment Note (memo)</span>
                                  <span className="text-white font-mono">{order.order_number}</span>
                                </div>
                                {order.zelle_confirmation_number && (
                                  <div className="flex justify-between text-xs">
                                    <span className="text-[#A1A7B5]">Zelle Confirmation #</span>
                                    <span className="text-white font-mono">{order.zelle_confirmation_number}</span>
                                  </div>
                                )}
                                {order.payment_verified_at && (
                                  <div className="flex justify-between text-xs">
                                    <span className="text-[#A1A7B5]">Payment Verified</span>
                                    <span className="text-green-400">{new Date(order.payment_verified_at).toLocaleString()}</span>
                                  </div>
                                )}
                                {order.affiliate_ref && (
                                  <div className="flex justify-between text-xs">
                                    <span className="text-[#A1A7B5]">Affiliate / Code</span>
                                    <span className="text-purple-400 font-mono">{order.affiliate_ref}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Shipping Address */}
                        <div>
                          <h4 className="text-[#A1A7B5] text-sm mb-3">Shipping Address</h4>
                          {order.shipping_address ? (
                            <div className="text-sm text-white space-y-1">
                              <p>{order.shipping_address.firstName} {order.shipping_address.lastName}</p>
                              <p className="text-[#A1A7B5]">{order.shipping_address.address}</p>
                              <p className="text-[#A1A7B5]">
                                {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zipCode}
                              </p>
                              <p className="text-[#A1A7B5]">{order.shipping_address.country}</p>
                            </div>
                          ) : (
                            <p className="text-[#A1A7B5] text-sm">No address provided</p>
                          )}
                          {order.tracking_number && (
                            <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg" data-testid={`tracking-${order.id}`}>
                              <p className="text-[#A1A7B5] text-xs mb-1">Tracking Number</p>
                              <a
                                href={order.tracking_url || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#00F0FF] font-mono text-sm hover:underline"
                              >
                                {order.tracking_number}
                              </a>
                            </div>
                          )}
                          {order.is_guest && (
                            <div className="mt-3">
                              <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full">Guest Order</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex justify-center items-center space-x-4 mt-8">
            <Button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="btn-secondary text-sm"
            >
              Previous
            </Button>
            <span className="text-[#A1A7B5]">Page {page} of {pages}</span>
            <Button
              onClick={() => setPage(Math.min(pages, page + 1))}
              disabled={page === pages}
              className="btn-secondary text-sm"
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrdersPage;
